import { lazy, Suspense, useEffect, useSyncExternalStore } from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '@/store/store';
import { getAudioOverviewPlayer } from '../player/audioOverviewPlayer';

// The bar and its sheet, menu and slider load with the first episode, not
// with the app shell.
const PersistentAudioBar = lazy(() => import('./PersistentAudioBar'));

interface AudioOverviewBarHostProps {
  pathname: string;
  /** Opens the Audio Overviews library; the host sits outside the router. */
  onOpenLibrary?: () => void;
  /** Opens a Library article (article episodes' transcript sources). */
  onOpenArticle?: (articleId: string) => void;
}

/**
 * Mounted in `App` beside the router so playback and its controls survive
 * every route change. Re-renders only when a track loads or unloads.
 */
export const AudioOverviewBarHost = ({
  pathname,
  onOpenLibrary,
  onOpenArticle,
}: AudioOverviewBarHostProps) => {
  const player = getAudioOverviewPlayer();

  const hasTrack = useSyncExternalStore(
    player.subscribe,
    () => player.getState().track !== null,
    () => false
  );

  const isSignedIn = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );

  // Logout clears the player too, but an expired session (`authFailed`) only
  // flips this flag: the episode must not keep playing with no controls.
  useEffect(() => {
    if (!isSignedIn && hasTrack) player.close();
  }, [hasTrack, isSignedIn, player]);

  if (!hasTrack) return null;

  return (
    <Suspense fallback={null}>
      <PersistentAudioBar
        pathname={pathname}
        isSignedIn={isSignedIn}
        onOpenLibrary={onOpenLibrary}
        onOpenArticle={onOpenArticle}
      />
    </Suspense>
  );
};

export default AudioOverviewBarHost;
